<template>
  <div class="operation-audit-logs">
    <div class="page-header">
      <h2>操作日志</h2>
      <p>查看关键业务操作留痕和前后变更内容</p>
    </div>

    <div class="search-section">
      <input v-model="actionType" class="search-input" placeholder="动作类型，如 DEVICE_START" @keyup.enter="loadLogs" />
      <input v-model="businessType" class="search-input" placeholder="业务类型，如 DEVICE_USAGE" @keyup.enter="loadLogs" />
      <select v-model.number="pageSize" class="search-select" @change="handleSearch">
        <option :value="20">20条/页</option>
        <option :value="50">50条/页</option>
        <option :value="100">100条/页</option>
      </select>
      <button class="search-btn" @click="handleSearch">搜索</button>
      <button class="reset-btn" @click="handleReset">重置</button>
    </div>

    <div class="table-section">
      <table class="data-table">
        <thead>
          <tr>
            <th>时间</th>
            <th>操作人</th>
            <th>动作</th>
            <th>业务</th>
            <th>业务ID</th>
            <th>门店ID</th>
            <th>原因</th>
            <th>结果</th>
            <th>变更摘要</th>
            <th>详情</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="10" class="empty-cell">加载中...</td>
          </tr>
          <tr v-else-if="logs.length === 0">
            <td colspan="10" class="empty-cell">暂无数据</td>
          </tr>
          <tr v-for="item in logs" :key="item.id">
            <td>{{ formatDate(item.createdTime) }}</td>
            <td>{{ item.operatorName || 'SYSTEM' }}</td>
            <td>{{ item.actionType }}</td>
            <td>{{ item.businessType }}</td>
            <td>{{ item.businessId || '-' }}</td>
            <td>{{ item.storeId || '-' }}</td>
            <td>{{ item.reason || '-' }}</td>
            <td>{{ item.success ? '成功' : '失败' }}</td>
            <td>{{ buildChangeSummary(item) }}</td>
            <td>
              <button class="detail-btn" @click="openDetail(item)">查看</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="pagination" v-if="totalPages > 0">
        <button class="page-btn" :disabled="page === 0 || loading" @click="changePage(page - 1)">上一页</button>
        <span class="page-text">第 {{ page + 1 }} / {{ totalPages }} 页，共 {{ totalElements }} 条</span>
        <button class="page-btn" :disabled="page >= totalPages - 1 || loading" @click="changePage(page + 1)">下一页</button>
      </div>
    </div>

    <div v-if="selectedLog" class="detail-mask" @click.self="closeDetail">
      <div class="detail-dialog">
        <div class="detail-header">
          <div>
            <h3>日志详情</h3>
            <p>{{ selectedLog.actionType }} / {{ selectedLog.businessType }}</p>
          </div>
          <button class="close-btn" @click="closeDetail">关闭</button>
        </div>

        <div class="meta-grid">
          <div class="meta-item"><span>时间</span><strong>{{ formatDate(selectedLog.createdTime) }}</strong></div>
          <div class="meta-item"><span>操作人</span><strong>{{ selectedLog.operatorName || 'SYSTEM' }}</strong></div>
          <div class="meta-item"><span>业务ID</span><strong>{{ selectedLog.businessId || '-' }}</strong></div>
          <div class="meta-item"><span>门店ID</span><strong>{{ selectedLog.storeId || '-' }}</strong></div>
        </div>

        <div class="diff-section">
          <div class="diff-title">字段变更</div>
          <div v-if="selectedDiffRows.length === 0" class="empty-diff">当前日志没有可对比的结构化字段</div>
          <table v-else class="diff-table">
            <thead>
              <tr>
                <th>字段</th>
                <th>变更前</th>
                <th>变更后</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in selectedDiffRows" :key="row.field">
                <td>{{ row.field }}</td>
                <td>{{ row.before }}</td>
                <td>{{ row.after }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="json-panels">
          <div class="json-panel">
            <div class="json-title">Before</div>
            <pre>{{ formatPayload(selectedLog.beforeData) }}</pre>
          </div>
          <div class="json-panel">
            <div class="json-title">After</div>
            <pre>{{ formatPayload(selectedLog.afterData) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'OperationAuditLogs',
  data() {
    return {
      logs: [],
      loading: false,
      actionType: '',
      businessType: '',
      page: 0,
      pageSize: 20,
      totalPages: 0,
      totalElements: 0,
      selectedLog: null,
      selectedDiffRows: []
    }
  },
  mounted() {
    this.loadLogs()
  },
  methods: {
    async loadLogs() {
      this.loading = true
      try {
        const response = await request.get('/api/operation-audit-logs/query', {
          params: {
            page: this.page,
            size: this.pageSize,
            actionType: this.actionType || undefined,
            businessType: this.businessType || undefined
          }
        })
        if (response.data.success) {
          const pageData = response.data.data || {}
          this.logs = pageData.content || []
          this.totalPages = pageData.totalPages || 0
          this.totalElements = pageData.totalElements || 0
        }
      } catch (error) {
        console.error('获取操作日志失败:', error)
        this.logs = []
        this.totalPages = 0
        this.totalElements = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 0
      this.loadLogs()
    },
    handleReset() {
      this.actionType = ''
      this.businessType = ''
      this.page = 0
      this.pageSize = 20
      this.loadLogs()
    },
    changePage(nextPage) {
      this.page = nextPage
      this.loadLogs()
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },
    parsePayload(payload) {
      if (!payload) return null
      try {
        return JSON.parse(payload)
      } catch (error) {
        return payload
      }
    },
    formatPayload(payload) {
      const parsed = this.parsePayload(payload)
      if (!parsed) return '无'
      if (typeof parsed === 'string') return parsed
      return JSON.stringify(parsed, null, 2)
    },
    openDetail(item) {
      this.selectedLog = item
      this.selectedDiffRows = this.buildDiffRows(item)
    },
    closeDetail() {
      this.selectedLog = null
      this.selectedDiffRows = []
    },
    buildDiffRows(item) {
      const before = this.parsePayload(item.beforeData)
      const after = this.parsePayload(item.afterData)
      if (!this.isPlainObject(before) || !this.isPlainObject(after)) {
        return []
      }
      const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))
      return keys
        .filter(key => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
        .slice(0, 20)
        .map(key => ({
          field: key,
          before: this.stringifyValue(before[key]),
          after: this.stringifyValue(after[key])
        }))
    },
    buildChangeSummary(item) {
      const rows = this.buildDiffRows(item)
      if (rows.length === 0) {
        return item.afterData ? '已记录结果数据' : '-'
      }
      const names = rows.slice(0, 3).map(row => row.field).join('、')
      return rows.length > 3 ? `${names} 等 ${rows.length} 项` : names
    },
    stringifyValue(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    isPlainObject(value) {
      return Object.prototype.toString.call(value) === '[object Object]'
    }
  }
}
</script>

<style scoped>
.operation-audit-logs {
  height: 100%;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px;
}

.page-header p {
  margin: 0;
  color: #666;
}

.search-section {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-input {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  min-width: 240px;
}

.search-select {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  min-width: 120px;
}

.search-btn,
.reset-btn {
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.search-btn {
  background: #1677ff;
  color: #fff;
}

.reset-btn {
  background: #f2f4f7;
}

.table-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th,
.data-table td {
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  text-align: left;
  font-size: 14px;
  vertical-align: top;
}

.empty-cell {
  text-align: center;
  color: #999;
}

.detail-btn,
.page-btn,
.close-btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.detail-btn {
  height: 32px;
  padding: 0 12px;
  background: #eff6ff;
  color: #1677ff;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
}

.page-btn {
  height: 34px;
  padding: 0 14px;
  background: #f8fafc;
}

.page-btn:disabled,
.close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.page-text {
  font-size: 13px;
  color: #64748b;
}

.detail-mask {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.detail-dialog {
  width: min(1100px, 100%);
  max-height: 90vh;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  overflow: auto;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.detail-header h3 {
  margin: 0 0 6px;
}

.detail-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  height: 34px;
  padding: 0 14px;
  background: #f1f5f9;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.meta-item {
  padding: 14px;
  border-radius: 10px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-item span {
  font-size: 12px;
  color: #64748b;
}

.meta-item strong {
  color: #0f172a;
}

.diff-section {
  margin-bottom: 20px;
}

.diff-title,
.json-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #1f2937;
}

.empty-diff {
  color: #94a3b8;
  padding: 16px 0;
}

.diff-table {
  width: 100%;
  border-collapse: collapse;
}

.diff-table th,
.diff-table td {
  padding: 10px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  font-size: 13px;
  vertical-align: top;
}

.json-panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.json-panel {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  background: #f8fafc;
}

.json-panel pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  color: #334155;
  line-height: 1.6;
}

@media (max-width: 960px) {
  .meta-grid,
  .json-panels {
    grid-template-columns: 1fr;
  }
}
</style>
